import { isNullable } from '@app/utils'

import { SELECT_PASTE_UNMATCHED_LIMIT } from './constants'

import type { FilterFunc } from 'rc-select/lib/Select'
import type { FilterOption } from '@app/types/filter'
import type { DefaultOptionType } from 'antd/es/select'
import type { LntSelectOption } from './types'

export const PASTE_TOKEN_SEPARATOR = /[\r\n\t]+/

const getFilteredValues = (values: unknown[], options?: FilterOption[]): unknown[] =>
  values.filter(value => options?.find(option => option.value === value))

export const getFilteredValue = (value: unknown[] | unknown, options?: FilterOption[]): unknown =>
  Array.isArray(value) ? getFilteredValues(value, options) : options?.find(option => option.value === value)?.value

export const filterOptionsByLabel: FilterFunc<DefaultOptionType> = (input, option) =>
  String(option?.label ?? '')
    .toLowerCase()
    .includes(input.toLowerCase())

export const getLabelsByValues = (values: unknown[], options?: FilterOption[]): Array<FilterOption['label']> | null => {
  if (isNullable(options)) return null

  return values.map(value => {
    return options.find(option => option.value === value)?.label
  })
}

export const parseClipboardTokens = (text: string): string[] =>
  text
    .split(PASTE_TOKEN_SEPARATOR)
    .map(token => token.trim())
    .filter(Boolean)

const normalizePasteToken = (token: string): string => token.trim().toLowerCase()

const isOptionMatchedByToken = (option: LntSelectOption, normalizedToken: string): boolean => {
  const valueMatch =
    String(option.value ?? '')
      .trim()
      .toLowerCase() === normalizedToken
  const labelMatch = typeof option.label === 'string' && option.label.trim().toLowerCase() === normalizedToken

  return valueMatch || labelMatch
}

export interface MatchPasteTokensResult {
  matchedValues: unknown[]
  unmatched: string[]
}

export const matchPasteTokens = (tokens: string[], options?: LntSelectOption[]): MatchPasteTokensResult => {
  if (!tokens.length) {
    return { matchedValues: [], unmatched: [] }
  }

  const matchedValues: unknown[] = []
  const unmatched: string[] = []
  const seenValues = new Set<unknown>()
  const seenUnmatched = new Set<string>()

  tokens.forEach(token => {
    const normalizedToken = normalizePasteToken(token)
    const matchedOptions = (options ?? []).filter(option => isOptionMatchedByToken(option, normalizedToken))

    if (!matchedOptions.length) {
      if (!seenUnmatched.has(normalizedToken)) {
        seenUnmatched.add(normalizedToken)
        unmatched.push(token)
      }

      return
    }

    matchedOptions.forEach(option => {
      if (seenValues.has(option.value)) return

      seenValues.add(option.value)
      matchedValues.push(option.value)
    })
  })

  return { matchedValues, unmatched }
}

export const formatUnmatched = (unmatched: string[]): string => {
  const visible = unmatched.slice(0, SELECT_PASTE_UNMATCHED_LIMIT)
  const restCount = unmatched.length - visible.length

  if (restCount <= 0) {
    return visible.join(', ')
  }

  return `${visible.join(', ')} и ещё ${restCount}`
}
